import styles from './VideoPlaybackButton.module.scss';

import cx from 'classnames';
import React, { ButtonHTMLAttributes, Dispatch, RefObject, SetStateAction, useEffect, useState } from 'react';
import { useReducedMotion } from 'hooks/useReducedMotion';
import { useTranslation } from 'react-i18next';

export function VideoPlaybackButton({
  videoRef,
  className,
  setIsVideoManualControl,
  ...props
}: {
  videoRef: RefObject<HTMLVideoElement>;
  setIsVideoManualControl?: Dispatch<SetStateAction<boolean>>;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  const { t } = useTranslation();
  const shouldReduceMotion = useReducedMotion();
  const video = videoRef.current;
  const [paused, setPaused] = useState(video?.paused);
  const noButton = shouldReduceMotion || !video;

  useEffect(() => {
    if (noButton) return;

    const update = () => {
      setPaused(video.paused);
    };

    video.addEventListener('playing', update);
    video.addEventListener('pause', update);
    update();

    return () => {
      video.removeEventListener('playing', update);
      video.removeEventListener('pause', update);
    };
  }, [video, shouldReduceMotion, noButton]);

  if (noButton) return null;

  const togglePlayback = () => {
    if (video.paused) {
      video.play();
      setIsVideoManualControl?.(false);
    } else {
      video.pause();
      setIsVideoManualControl?.(true);
    }
  };

  return (
    <button className={cx(styles.root, className)} type="button" onClick={togglePlayback} {...props}>
      <span className="visually-hidden">{t(paused ? 'play' : 'pause')}</span>
      <svg
        aria-hidden="true"
        width="36"
        height="36"
        viewBox="0 0 36 36"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M18 33C26.2843 33 33 26.2843 33 18C33 9.71573 26.2843 3 18 3C9.71573 3 3 9.71573 3 18C3 26.2843 9.71573 33 18 33Z" />
        {paused ? (
          <path d="M23.625 18L13.625 13V23L23.625 18Z" />
        ) : (
          <>
            <path d="M15 22.5V13.5" />
            <path d="M21 22.5V13.5" />
          </>
        )}
      </svg>
    </button>
  );
}
